// src/components/projects/ProjectPlanner3D.jsx
// ✅ Piano 3D + AI Budget — Prezziario reale 2025 Europa + Africa
// ✅ 24 tipi struttura raggruppati: Residenziale + Commerciale + Infrastrutture
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { aiApi } from '../../api/aiApi';
import { formatCurrency } from '../../utils/formatters';
import {
    STRUCTURE_TYPES, STRUCTURE_CATEGORIES,
    BUDGET_BREAKDOWN, BREAKDOWN_LABELS, BREAKDOWN_COLORS,
    estimateBudget, compareBudgetToMarket,
} from '../../utils/constructionCosts';
import {
    SparklesIcon, CubeIcon, ArrowPathIcon,
    ChevronRightIcon, ChevronLeftIcon,
    CheckCircleIcon, ExclamationTriangleIcon,
    InformationCircleIcon,
} from '@heroicons/react/24/outline';

// ==================== 3D CANVAS ====================
const CANVAS_SHAPES = {
    residential: (ctx, cx, cy, iso, angle) => {
        // Casa generica
        const W = 70, H = 50, D = 60, ROOF = 30;
        const face = (pts, color) => { ctx.beginPath(); ctx.moveTo(pts[0].sx, pts[0].sy); pts.slice(1).forEach(p => ctx.lineTo(p.sx, p.sy)); ctx.closePath(); ctx.fillStyle = color; ctx.fill(); ctx.strokeStyle = 'rgba(255,255,255,0.15)'; ctx.lineWidth = 1; ctx.stroke(); };
        face([iso(-W, 0, -D), iso(W, 0, -D), iso(W, -H, -D), iso(-W, -H, -D)], '#2563eb');
        face([iso(W, 0, -D), iso(W, 0, D), iso(W, -H, D), iso(W, -H, -D)], '#1d4ed8');
        face([iso(-W, -H, -D), iso(W, -H, -D), iso(W, -H, D), iso(-W, -H, D)], '#3b82f6');
        face([iso(-W, -H, -D), iso(0, -H - ROOF, -D * 0.5), iso(W, -H, -D)], '#475569');
        face([iso(-W, -H, D), iso(0, -H - ROOF, -D * 0.5), iso(W, -H, D)], '#64748b');
        face([iso(-W, -H, -D), iso(-W, -H, D), iso(0, -H - ROOF, -D * 0.5)], '#94a3b8');
        face([iso(W, -H, -D), iso(W, -H, D), iso(0, -H - ROOF, -D * 0.5)], '#64748b');
        for (let i = -1; i <= 1; i++) { const p = iso(i * 25, -H * 0.5, -D - 1); ctx.fillStyle = '#fbbf24'; ctx.fillRect(p.sx - 7, p.sy - 8, 14, 10); }
    },
    commercial: (ctx, cx, cy, iso, angle) => {
        // Edificio commerciale
        const BW = 80, BH = 80, BD = 60;
        const face = (pts, color) => { ctx.beginPath(); ctx.moveTo(pts[0].sx, pts[0].sy); pts.slice(1).forEach(p => ctx.lineTo(p.sx, p.sy)); ctx.closePath(); ctx.fillStyle = color; ctx.fill(); ctx.strokeStyle = 'rgba(255,255,255,0.15)'; ctx.lineWidth = 1; ctx.stroke(); };
        face([iso(-BW, 0, -BD), iso(BW, 0, -BD), iso(BW, -BH, -BD), iso(-BW, -BH, -BD)], '#7c3aed');
        face([iso(BW, 0, -BD), iso(BW, 0, BD), iso(BW, -BH, BD), iso(BW, -BH, -BD)], '#6d28d9');
        face([iso(-BW, -BH, -BD), iso(BW, -BH, -BD), iso(BW, -BH, BD), iso(-BW, -BH, BD)], '#8b5cf6');
        for (let f = 0; f < 4; f++) for (let w = -2; w <= 2; w++) { const p = iso(w * 28, -f * 18 - 10, -BD - 1); ctx.fillStyle = f % 2 === 0 ? '#fbbf24' : '#a5f3fc'; ctx.fillRect(p.sx - 8, p.sy - 7, 16, 10); }
    },
    infrastructure: (ctx, cx, cy, iso, angle) => {
        // Strada con linea centrale
        const L = 130, W2 = 35, T = 6;
        const face = (pts, color) => { ctx.beginPath(); ctx.moveTo(pts[0].sx, pts[0].sy); pts.slice(1).forEach(p => ctx.lineTo(p.sx, p.sy)); ctx.closePath(); ctx.fillStyle = color; ctx.fill(); ctx.strokeStyle = 'rgba(255,255,255,0.15)'; ctx.lineWidth = 1; ctx.stroke(); };
        face([iso(-L, 0, -W2), iso(L, 0, -W2), iso(L, 0, W2), iso(-L, 0, W2)], '#475569');
        face([iso(-L, 0, -W2), iso(L, 0, -W2), iso(L, T, -W2), iso(-L, T, -W2)], '#64748b');
        face([iso(-L, T, -W2), iso(L, T, -W2), iso(L, T, W2), iso(-L, T, W2)], '#94a3b8');
        ctx.setLineDash([15, 10]); ctx.strokeStyle = '#fbbf24'; ctx.lineWidth = 2;
        const p1 = iso(-L * 0.8, T + 1, 0), p2 = iso(L * 0.8, T + 1, 0);
        ctx.beginPath(); ctx.moveTo(p1.sx, p1.sy); ctx.lineTo(p2.sx, p2.sy); ctx.stroke(); ctx.setLineDash([]);
    },
};

const Structure3D = ({ category }) => {
    const canvasRef = useRef(null);
    const animRef = useRef(null);
    const angleRef = useRef(0);

    const draw = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        const W = canvas.width, H = canvas.height;
        ctx.clearRect(0, 0, W, H);
        const bg = ctx.createLinearGradient(0, 0, 0, H);
        bg.addColorStop(0, '#0f172a'); bg.addColorStop(1, '#1e293b');
        ctx.fillStyle = bg; ctx.fillRect(0, 0, W, H);
        ctx.strokeStyle = 'rgba(148,163,184,0.06)'; ctx.lineWidth = 1;
        for (let x = 0; x < W; x += 30) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke(); }
        for (let y = 0; y < H; y += 30) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke(); }
        const cx = W / 2, cy = H / 2 + 20, angle = angleRef.current;
        const cos = Math.cos(angle), sin = Math.sin(angle);
        const iso = (x, y, z) => ({ sx: cx + (x * cos - z * sin) * 0.8, sy: cy + (x * sin + z * cos) * 0.3 - y * 0.9 });
        const shapeFn = CANVAS_SHAPES[category] || CANVAS_SHAPES.residential;
        shapeFn(ctx, cx, cy, iso, angle);
        angleRef.current += 0.005;
        animRef.current = requestAnimationFrame(draw);
    }, [category]);

    useEffect(() => { animRef.current = requestAnimationFrame(draw); return () => cancelAnimationFrame(animRef.current); }, [draw]);
    return <canvas ref={canvasRef} width={400} height={240} className="w-full rounded-xl" style={{ background: '#0f172a' }} />;
};

// ==================== BUDGET CHART ====================
const BudgetChart = ({ breakdown, budget, currency }) => {
    const [hovered, setHovered] = useState(null);
    return (
        <div className="space-y-2">
            {Object.entries(breakdown).map(([cat, pct]) => {
                const amount = (budget * pct) / 100;
                const color = BREAKDOWN_COLORS[cat];
                return (
                    <div key={cat} onMouseEnter={() => setHovered(cat)} onMouseLeave={() => setHovered(null)}>
                        <div className="flex items-center justify-between mb-0.5">
                            <div className="flex items-center gap-1.5">
                                <div className="w-2 h-2 rounded-full" style={{ background: color }} />
                                <span className="text-xs text-gray-600">{BREAKDOWN_LABELS[cat]}</span>
                            </div>
                            <div className="text-right">
                                <span className="text-xs font-bold text-gray-900">{pct}%</span>
                                <span className="text-xs text-gray-400 ml-1.5">{formatCurrency(amount, currency)}</span>
                            </div>
                        </div>
                        <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                            <div className="h-full rounded-full transition-all duration-500"
                                style={{ width: `${pct}%`, background: color, opacity: hovered === cat ? 1 : 0.75 }} />
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

// ==================== MARKET ESTIMATE CARD ====================
const MarketEstimateCard = ({ estimate, userBudget, currency }) => {
    if (!estimate) return null;
    const comparison = userBudget ? compareBudgetToMarket(userBudget, estimate.estimated, currency) : null;
    return (
        <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl space-y-2">
            <div className="flex items-center gap-2">
                <InformationCircleIcon className="w-4 h-4 text-slate-500" />
                <p className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Stima di mercato reale 2025</p>
            </div>
            <div className="flex items-end justify-between">
                <div>
                    <p className="text-lg font-bold text-gray-900">{formatCurrency(estimate.estimated, currency)}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{estimate.note}</p>
                    {estimate.calc && <p className="text-xs text-gray-300 mt-0.5 italic">{estimate.calc}</p>}
                </div>
                {comparison && (
                    <span className="text-xs font-bold px-2 py-1 rounded-full" style={{ background: comparison.color + '20', color: comparison.color }}>
                        {comparison.label}
                    </span>
                )}
            </div>
            {comparison && <p className="text-xs" style={{ color: comparison.color }}>{comparison.msg}</p>}
            <p className="text-xs text-gray-300">Fonte: ANCE Italia · AMC Guinée · Banque Mondiale · BOAD</p>
        </div>
    );
};

// ==================== MAIN ====================
const ProjectPlanner3D = ({ budget, currency, onPlanGenerated, onClose }) => {
    const { organization } = useAuth();
    const [step, setStep] = useState(1);
    const [selectedType, setSelectedType] = useState(null);
    const [dimensions, setDimensions] = useState({ length: '', width: '', floors: '', area: '' });
    const [aiAnalysis, setAiAnalysis] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const usedCurrency = currency || organization?.currency || 'EUR';
    const budgetNum = parseFloat(budget) || 0;
    const selectedTypeData = STRUCTURE_TYPES.find(t => t.id === selectedType);
    const breakdown = selectedType ? BUDGET_BREAKDOWN[selectedType] : null;

    const marketEstimate = selectedType && (dimensions.length || dimensions.floors || dimensions.area || selectedTypeData?.isForfait)
        ? estimateBudget(selectedType, dimensions, usedCurrency) : null;

    const generateAIPlan = async () => {
        setLoading(true); setError(null);
        try {
            const res = await aiApi.plannerAnalysis({
                structureType: selectedType,
                structureLabel: selectedTypeData?.label,
                dimensions,
                budget: budgetNum,
                marketEstimate: marketEstimate?.estimated,
                currency: usedCurrency,
                organizationName: organization?.name,
            });
            setAiAnalysis(res.data);
            setStep(3);
        } catch (err) {
            const code = err?.response?.data?.code;
            if (code === 'AI_PLAN_UPGRADE_REQUIRED') {
                setError("Funzionalità AI non disponibile nel piano attuale. Effettua l'upgrade.");
            } else if (code === 'AI_QUOTA_EXCEEDED') {
                setError('Limite AI mensile raggiunto.');
            } else {
                // Fallback graceful senza AI
                setAiAnalysis({
                    rischi: ['Variazioni meteo', 'Forniture materiali', 'Burocrazia permessi'],
                    raccomandazioni: ['Aggiungere buffer 10% al budget', 'Pianificare i permessi in anticipo', 'Selezionare fornitori certificati'],
                    fasi: [
                        { nome: 'Progettazione', durata: '2 mesi', percentualeBudget: 8 },
                        { nome: 'Preparazione', durata: '1 mese', percentualeBudget: 12 },
                        { nome: 'Costruzione', durata: '6 mesi', percentualeBudget: 65 },
                        { nome: 'Finitura e collaudo', durata: '2 mesi', percentualeBudget: 15 },
                    ],
                    budgetRiserva: 10, complessita: 'MEDIA',
                });
                setStep(3);
            }
        } finally { setLoading(false); }
    };

    const handleApplyPlan = () => {
        if (!breakdown || !budgetNum) return;
        onPlanGenerated?.({
            structureType: selectedType, dimensions,
            breakdown: Object.entries(breakdown).map(([cat, pct]) => ({
                category: cat, label: BREAKDOWN_LABELS[cat],
                percentage: pct, estimatedAmount: (budgetNum * pct) / 100,
            })),
            aiAnalysis, marketEstimate,
            totalBudget: budgetNum, currency: usedCurrency,
        });
        onClose?.();
    };

    const dimInput = (label, key, placeholder) => (
        <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">{label}</label>
            <input type="number" value={dimensions[key]}
                onChange={e => setDimensions(prev => ({ ...prev, [key]: e.target.value }))}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder={placeholder} />
        </div>
    );

    // Dimensioni richieste per tipo
    const needsDimensions = selectedTypeData && !['villa', 'duplex', 'maison', 'bungalow', 'appartamento', 'boutique', 'pont', 'tunnel', 'barrage'].includes(selectedType);

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">

                {/* Header */}
                <div className="bg-gradient-to-r from-slate-900 to-blue-900 px-6 py-4 flex items-center justify-between flex-shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 bg-blue-500/30 rounded-xl flex items-center justify-center">
                            <CubeIcon className="w-5 h-5 text-blue-300" />
                        </div>
                        <div>
                            <h2 className="text-base font-bold text-white">Piano 3D + AI Budget</h2>
                            <p className="text-blue-300 text-xs">Prezziario reale 2025 · {usedCurrency}</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        {[1, 2, 3].map(s => (
                            <div key={s} className={`w-2.5 h-2.5 rounded-full transition-all ${s === step ? 'bg-blue-400 scale-125' : s < step ? 'bg-emerald-400' : 'bg-slate-600'}`} />
                        ))}
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto">

                    {/* STEP 1 — Selezione tipo struttura raggruppata per categoria */}
                    {step === 1 && (
                        <div className="p-6 space-y-6">
                            <div>
                                <h3 className="text-lg font-bold text-gray-900 mb-1">Che tipo di struttura stai costruendo?</h3>
                                <p className="text-gray-500 text-sm">Seleziona per ottenere la stima basata su prezzi reali di mercato 2025</p>
                            </div>

                            {STRUCTURE_CATEGORIES.map(cat => {
                                const types = STRUCTURE_TYPES.filter(t => t.category === cat.id);
                                return (
                                    <div key={cat.id}>
                                        <div className="flex items-center gap-2 mb-3">
                                            <span className="text-lg">{cat.icon}</span>
                                            <h4 className="text-sm font-bold text-gray-700 uppercase tracking-wider">{cat.label}</h4>
                                            <div className="flex-1 h-px bg-gray-100" />
                                        </div>
                                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                                            {types.map(type => (
                                                <button key={type.id} onClick={() => setSelectedType(type.id)}
                                                    className={`p-3 rounded-xl border-2 text-left transition-all ${selectedType === type.id
                                                            ? 'border-blue-500 bg-blue-50 shadow-sm'
                                                            : 'border-gray-100 hover:border-blue-200 hover:bg-gray-50'
                                                        }`}>
                                                    <span className="text-2xl block mb-1.5">{type.icon}</span>
                                                    <p className={`text-xs font-semibold leading-tight ${selectedType === type.id ? 'text-blue-700' : 'text-gray-900'}`}>{type.label}</p>
                                                    <p className="text-xs text-gray-400 mt-0.5 leading-tight line-clamp-2">{type.desc}</p>
                                                    {selectedType === type.id && <CheckCircleIcon className="w-3.5 h-3.5 text-blue-500 mt-1.5" />}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* STEP 2 — Dimensioni + Preview 3D */}
                    {step === 2 && selectedTypeData && (
                        <div className="p-6">
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                <div>
                                    <div className="flex items-center gap-2 mb-4">
                                        <span className="text-2xl">{selectedTypeData.icon}</span>
                                        <div>
                                            <h3 className="text-base font-bold text-gray-900">{selectedTypeData.label}</h3>
                                            <p className="text-xs text-gray-400">{selectedTypeData.desc}</p>
                                        </div>
                                    </div>

                                    {/* Dimensioni — solo se non forfait */}
                                    {needsDimensions && (
                                        <div className="space-y-3 mb-4">
                                            <h4 className="text-sm font-semibold text-gray-700">Inserisci le dimensioni</h4>
                                            {['r1', 'r2', 'r3', 'r4plus', 'immeuble', 'bureau', 'hotel', 'ecole', 'clinique', 'hangar'].includes(selectedType) && (
                                                <>{dimInput('Numero di piani', 'floors', 'es. 3')}{dimInput('Superficie per piano (m²)', 'area', 'es. 200')}</>
                                            )}
                                            {['route', 'autoroute', 'reseau'].includes(selectedType) && (
                                                <>{dimInput('Lunghezza (km)', 'length', 'es. 5')}{dimInput('Larghezza (m)', 'width', 'es. 7')}</>
                                            )}
                                            {['fondations'].includes(selectedType) && (
                                                <>{dimInput('Superficie (m²)', 'area', 'es. 500')}</>
                                            )}
                                            {['cloture'].includes(selectedType) && (
                                                <>{dimInput('Lunghezza recinzione (ml)', 'length', 'es. 200')}</>
                                            )}
                                        </div>
                                    )}

                                    {/* Stima mercato */}
                                    {(marketEstimate || selectedTypeData) && (
                                        <MarketEstimateCard
                                            estimate={marketEstimate || estimateBudget(selectedType, {}, usedCurrency)}
                                            userBudget={budgetNum}
                                            currency={usedCurrency}
                                        />
                                    )}

                                    {/* Breakdown budget */}
                                    {budgetNum > 0 && breakdown && (
                                        <div className="mt-4 p-4 bg-blue-50 border border-blue-100 rounded-xl">
                                            <p className="text-xs font-semibold text-blue-700 mb-3 uppercase tracking-wider">Distribuzione budget</p>
                                            <BudgetChart breakdown={breakdown} budget={budgetNum} currency={usedCurrency} />
                                        </div>
                                    )}
                                </div>

                                <div>
                                    <h3 className="text-base font-bold text-gray-900 mb-4">Anteprima 3D</h3>
                                    <Structure3D category={selectedTypeData.category} />
                                    <p className="text-xs text-gray-400 text-center mt-2">Rotazione automatica · Rappresentazione schematica</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* STEP 3 — Risultati AI */}
                    {step === 3 && aiAnalysis && breakdown && (
                        <div className="p-6">
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                <div>
                                    <div className="flex items-center gap-2 mb-4">
                                        <SparklesIcon className="w-5 h-5 text-purple-500" />
                                        <h3 className="text-base font-bold text-gray-900">Piano Budget AI</h3>
                                        <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${aiAnalysis.complessita === 'ALTA' ? 'bg-red-100 text-red-700' : aiAnalysis.complessita === 'MEDIA' ? 'bg-yellow-100 text-yellow-700' : 'bg-emerald-100 text-emerald-700'}`}>
                                            {aiAnalysis.complessita}
                                        </span>
                                    </div>

                                    {marketEstimate && <MarketEstimateCard estimate={marketEstimate} userBudget={budgetNum} currency={usedCurrency} />}

                                    <div className="bg-gray-50 rounded-xl p-4 mt-4 mb-4">
                                        <BudgetChart breakdown={breakdown} budget={budgetNum} currency={usedCurrency} />
                                        <div className="mt-3 pt-3 border-t border-gray-200 space-y-1">
                                            <div className="flex justify-between">
                                                <span className="text-xs text-gray-500">Budget operativo</span>
                                                <span className="text-sm font-bold text-gray-900">{formatCurrency(budgetNum * (1 - aiAnalysis.budgetRiserva / 100), usedCurrency)}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-xs text-gray-500">Riserva {aiAnalysis.budgetRiserva}%</span>
                                                <span className="text-sm font-bold text-orange-600">{formatCurrency(budgetNum * aiAnalysis.budgetRiserva / 100, usedCurrency)}</span>
                                            </div>
                                        </div>
                                    </div>

                                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Fasi di progetto</p>
                                    <div className="space-y-2">
                                        {aiAnalysis.fasi?.map((fase, i) => (
                                            <div key={i} className="flex items-center gap-3 p-2.5 bg-white border border-gray-100 rounded-lg">
                                                <div className="w-6 h-6 bg-blue-100 rounded-lg flex items-center justify-center text-xs font-bold text-blue-700">{i + 1}</div>
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-xs font-semibold text-gray-900">{fase.nome}</p>
                                                    <p className="text-xs text-gray-400">{fase.durata}</p>
                                                </div>
                                                <div className="text-right">
                                                    <p className="text-xs font-bold text-gray-700">{fase.percentualeBudget}%</p>
                                                    <p className="text-xs text-gray-400">{formatCurrency(budgetNum * fase.percentualeBudget / 100, usedCurrency)}</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div>
                                    <div className="mb-4">
                                        <div className="flex items-center gap-2 mb-2">
                                            <ExclamationTriangleIcon className="w-4 h-4 text-yellow-500" />
                                            <p className="text-sm font-semibold text-gray-900">Rischi identificati</p>
                                        </div>
                                        <div className="space-y-2">
                                            {aiAnalysis.rischi?.map((r, i) => (
                                                <div key={i} className="flex items-start gap-2 p-2.5 bg-yellow-50 border border-yellow-100 rounded-lg">
                                                    <span className="text-yellow-500 text-xs mt-0.5">⚠️</span>
                                                    <p className="text-xs text-yellow-800">{r}</p>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="mb-4">
                                        <div className="flex items-center gap-2 mb-2">
                                            <CheckCircleIcon className="w-4 h-4 text-emerald-500" />
                                            <p className="text-sm font-semibold text-gray-900">Raccomandazioni AI</p>
                                        </div>
                                        <div className="space-y-2">
                                            {aiAnalysis.raccomandazioni?.map((r, i) => (
                                                <div key={i} className="flex items-start gap-2 p-2.5 bg-emerald-50 border border-emerald-100 rounded-lg">
                                                    <span className="text-emerald-500 text-xs mt-0.5">✅</span>
                                                    <p className="text-xs text-emerald-800">{r}</p>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    <Structure3D category={selectedTypeData?.category || 'residential'} />
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex items-center justify-between flex-shrink-0">
                    <button onClick={() => step > 1 ? setStep(step - 1) : onClose?.()}
                        className="flex items-center gap-2 px-4 py-2 border border-gray-200 text-gray-600 rounded-xl text-sm font-medium hover:bg-white transition">
                        <ChevronLeftIcon className="w-4 h-4" />
                        {step === 1 ? 'Annulla' : 'Indietro'}
                    </button>
                    <div className="flex items-center gap-3">
                        {error && <p className="text-xs text-red-500 max-w-xs text-right">{error}</p>}
                        {step === 1 && (
                            <button onClick={() => selectedType && setStep(2)} disabled={!selectedType}
                                className="flex items-center gap-2 px-5 py-2 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 transition disabled:opacity-40">
                                Continua <ChevronRightIcon className="w-4 h-4" />
                            </button>
                        )}
                        {step === 2 && (
                            <button onClick={generateAIPlan} disabled={loading || !budgetNum}
                                className="flex items-center gap-2 px-5 py-2 bg-purple-600 text-white rounded-xl text-sm font-semibold hover:bg-purple-700 transition disabled:opacity-40">
                                {loading
                                    ? <><ArrowPathIcon className="w-4 h-4 animate-spin" /> Analisi AI...</>
                                    : <><SparklesIcon className="w-4 h-4" /> Genera Piano AI</>}
                            </button>
                        )}
                        {step === 3 && (
                            <button onClick={handleApplyPlan}
                                className="flex items-center gap-2 px-5 py-2 bg-emerald-600 text-white rounded-xl text-sm font-semibold hover:bg-emerald-700 transition">
                                <CheckCircleIcon className="w-4 h-4" /> Applica Piano al Progetto
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProjectPlanner3D;